<script setup lang="ts">
	const props = defineProps({
		modalTitle: {
			type: String,
			required: false,
			default: '',
		},
		isOpen: {
			type: Boolean,
			required: true,
		},
	});

	const emit = defineEmits(['update:isOpen']);

	const closeModal = () => {
		unblockScroll();
		emit('update:isOpen', false);
	};

	const blockScroll = () => {
		const scrollbarWidth = window.innerWidth - document.documentElement?.clientWidth;
		document.body.style.setProperty('--padding-instead-scroll', `${scrollbarWidth}px`);
		document.body.style.overflow = 'hidden';
	};

	const unblockScroll = () => {
		document.body.style.overflow = '';
		document.body.style.setProperty('--padding-instead-scroll', `0px`);
	};

	onMounted(() => {
		if (props.isOpen) {
			blockScroll();
		} else {
			unblockScroll();
		}
	});

	watch(
		() => props.isOpen,
		(newVal) => {
			if (newVal) {
				blockScroll();
			} else {
				unblockScroll();
			}
		}
	);

	onBeforeUnmount(() => {
		unblockScroll();
	});
</script>

<template>
	<div>
		<transition name="fade">
			<div v-if="isOpen" class="modal-overlay" @click="closeModal"></div>
		</transition>
		<div class="modal-container" @click.self="closeModal">
			<transition name="slide">
				<div v-if="isOpen" class="modal">
					<div class="modal__header">
						<div class="modal__header-title">
							{{ modalTitle }}
						</div>
					</div>
					<button class="close-button" @click="closeModal">
						<Icon name="local:close" class="close-icon" />
					</button>
					<div class="modal-content">
						<slot></slot>
					</div>
				</div>
			</transition>
		</div>
	</div>
</template>

<style scoped>
	.modal-container {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: grid;
		justify-items: center;
		align-content: space-around;
		z-index: 999;
		width: 100%;
		height: 100%;
		overflow-x: hidden;
		overflow-y: auto;
		padding: var(--layout-padding);
		/* margin-top: var(--height-header); */
	}
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.5);
		z-index: 998;
	}

	.modal {
		padding: 27px 15px 15px;
		position: relative;
		background: white;
		z-index: 999;
		width: 100%;
		height: fit-content;
		max-width: calc(100vw - calc(2 * var(--layout-padding)));
		width: 588px;
		border-radius: 20px;
	}

	.modal__header {
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 15px;
	}

	.modal__header-title {
		font-size: 24px;
		font-weight: 700px;
		padding: 0px 36px;
	}

	.close-button {
		position: absolute;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		top: 25px;
		right: 15px;
		background: none;
		border: none;
		font-size: 20px;
		cursor: pointer;
		&:hover {
			color: var(--color-secondary-dark);
		}
	}

	.close-icon {
		width: 24px;
		height: 24px;
	}

	.modal-content {
		width: 100%;
		height: auto;
	}

	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.3s;
	}
	.fade-enter,
	.fade-leave-to {
		opacity: 0;
	}

	.slide-enter-active,
	.slide-leave-active {
		transition: transform 0.3s;
	}
	.slide-enter,
	.slide-leave-to {
		transform: translateY(-10%);
	}

	@media (min-width: 1240px) {
		.modal {
			padding: 35px 30px 30px;
			border-radius: 32px;
		}

		.modal__header {
			margin-bottom: 20px;
		}

		.modal__header-title {
			font-size: 30px;
		}

		.close-button {
			top: 44px;
			right: 30px;
		}

		.close-icon {
			width: 24px;
			height: 24px;
		}
	}
</style>
